import { useEffect, useRef, useState } from "react";
import {
    $createNodeSelection,
    $getNearestNodeFromDOMNode,
    $getNodeByKey,
    $getRoot,
    $getSelection,
    $isElementNode,
    $isNodeSelection,
    $setSelection,
} from "lexical";
import { $isTableNode, TableNode } from "@lexical/table";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Move } from "lucide-react";

const HANDLE_W = 22;
const HANDLE_H = 22;
const EDGE_THRESHOLD = 8;
const DRAG_THRESHOLD = 5;

interface HoverState {
    tableEl: HTMLTableElement;
}

interface DragCandidate {
    key: string;
    el: HTMLElement;
}

export function TableSelectHandle() {
    const [editor] = useLexicalComposerContext();
    const [hover, setHover] = useState<HoverState | null>(null);
    const [selectedTableKey, setSelectedTableKey] = useState<string | null>(null);
    const dragStateRef = useRef<{
        startX: number;
        startY: number;
        tableEl: HTMLTableElement;
        candidates: DragCandidate[];
        isDragging: boolean;
        dropTarget: { key: string; position: "before" | "after" } | null;
    } | null>(null);
    const dropLineRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const root = editor.getRootElement();
        if (!root) return;

        const onMouseMove = (e: MouseEvent) => {
            if (dragStateRef.current?.isDragging) return;

            const tables = root.querySelectorAll<HTMLTableElement>("table");
            let hit: HTMLTableElement | null = null;
            for (const t of tables) {
                const r = t.getBoundingClientRect();

                const inHandleArea =
                    e.clientX >= r.left - 2 &&
                    e.clientX <= r.left + HANDLE_W + 2 &&
                    e.clientY >= r.top - HANDLE_H - 2 &&
                    e.clientY <= r.top + 2;
                if (inHandleArea) {
                    hit = t;
                    break;
                }

                const withinX = e.clientX >= r.left - EDGE_THRESHOLD && e.clientX <= r.right + EDGE_THRESHOLD;
                const withinY = e.clientY >= r.top - EDGE_THRESHOLD && e.clientY <= r.bottom + EDGE_THRESHOLD;
                if (!withinX || !withinY) continue;

                const onTopEdge = Math.abs(e.clientY - r.top) <= EDGE_THRESHOLD;
                const onBottomEdge = Math.abs(e.clientY - r.bottom) <= EDGE_THRESHOLD;
                const onLeftEdge = Math.abs(e.clientX - r.left) <= EDGE_THRESHOLD;
                const onRightEdge = Math.abs(e.clientX - r.right) <= EDGE_THRESHOLD;
                if (onTopEdge || onBottomEdge || onLeftEdge || onRightEdge) {
                    hit = t;
                    break;
                }
            }
            setHover(hit ? { tableEl: hit } : null);
        };

        document.addEventListener("mousemove", onMouseMove);
        return () => document.removeEventListener("mousemove", onMouseMove);
    }, [editor]);

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const sel = $getSelection();
                if ($isNodeSelection(sel)) {
                    const tableNode = sel.getNodes().find((n) => $isTableNode(n));
                    setSelectedTableKey(tableNode ? tableNode.getKey() : null);
                } else {
                    setSelectedTableKey(null);
                }
            });
        });
    }, [editor]);

    const [ringBounds, setRingBounds] = useState<{
        top: number;
        left: number;
        width: number;
        height: number;
    } | null>(null);

    useEffect(() => {
        if (!selectedTableKey) {
            setRingBounds(null);
            return;
        }
        const computeBounds = () => {
            const el = editor.getElementByKey(selectedTableKey);
            if (!el) {
                setRingBounds(null);
                return;
            }
            const tableEl =
                el.tagName === "TABLE"
                    ? el
                    : (el.querySelector("table") as HTMLElement | null);
            const target = tableEl ?? el;
            const r = target.getBoundingClientRect();
            setRingBounds({ top: r.top, left: r.left, width: r.width, height: r.height });
        };
        computeBounds();
        const scheduledCompute = () => {
            requestAnimationFrame(computeBounds);
        };
        const unsubscribe = editor.registerUpdateListener(scheduledCompute);
        window.addEventListener("scroll", scheduledCompute, true);
        window.addEventListener("resize", scheduledCompute);
        return () => {
            unsubscribe();
            window.removeEventListener("scroll", scheduledCompute, true);
            window.removeEventListener("resize", scheduledCompute);
        };
    }, [editor, selectedTableKey]);

    useEffect(() => {
        if (!selectedTableKey) return;

        const onKeyDown = (e: KeyboardEvent) => {
            const meta = e.metaKey || e.ctrlKey;
            if (!meta) return;
            const key = e.key.toLowerCase();
            const isCopy = key === "c";
            const isCut = key === "x";
            if (!isCopy && !isCut) return;

            const el = editor.getElementByKey(selectedTableKey);
            if (!el) return;
            const tableEl =
                el.tagName === "TABLE"
                    ? (el as HTMLTableElement)
                    : (el.querySelector("table") as HTMLTableElement | null);
            if (!tableEl) return;

            e.preventDefault();

            const html = tableEl.outerHTML;
            const text = tableEl.innerText;

            const tryClipboard = async () => {
                try {
                    const item = new ClipboardItem({
                        "text/html": new Blob([html], { type: "text/html" }),
                        "text/plain": new Blob([text], { type: "text/plain" }),
                    });
                    await navigator.clipboard.write([item]);
                } catch {
                    try {
                        await navigator.clipboard.writeText(html);
                    } catch (err) {
                        console.error("Clipboard write failed:", err);
                    }
                }
            };
            void tryClipboard();

            if (isCut) {
                editor.update(() => {
                    $getNodeByKey(selectedTableKey)?.remove();
                });
            }
        };

        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [editor, selectedTableKey]);

    const findTableNode = (tableEl: HTMLTableElement): TableNode | undefined => {
        const node = $getNearestNodeFromDOMNode(tableEl);
        if (!node) return undefined;
        return $isTableNode(node)
            ? (node as TableNode)
            : (node.getParents().find((p) => $isTableNode(p)) as TableNode | undefined);
    };

    const onMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!hover) return;

        const tableEl = hover.tableEl;

        const candidates: DragCandidate[] = [];
        let tableKey: string | null = null;
        editor.read(() => {
            const tableNode = findTableNode(tableEl);
            if (!tableNode) return;
            tableKey = tableNode.getKey();
            const rootNode = $getRoot();
            for (const child of rootNode.getChildren()) {
                if (child.getKey() === tableKey) continue;
                const childEl = editor.getElementByKey(child.getKey());
                if (childEl) candidates.push({ key: child.getKey(), el: childEl });
            }
        });

        if (!tableKey) return;

        dragStateRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            tableEl,
            candidates,
            isDragging: false,
            dropTarget: null,
        };

        const onMove = (ev: MouseEvent) => {
            const state = dragStateRef.current;
            if (!state) return;
            const dx = ev.clientX - state.startX;
            const dy = ev.clientY - state.startY;
            if (!state.isDragging && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
            if (!state.isDragging) {
                state.isDragging = true;
                document.body.style.cursor = "grabbing";
                document.body.style.userSelect = "none";
            }

            let best: { c: DragCandidate; position: "before" | "after"; y: number } | null = null;
            let bestDist = Infinity;
            for (const c of state.candidates) {
                const r = c.el.getBoundingClientRect();
                const midY = r.top + r.height / 2;
                const dist = Math.abs(ev.clientY - midY);
                if (dist < bestDist) {
                    bestDist = dist;
                    best = {
                        c,
                        position: ev.clientY < midY ? "before" : "after",
                        y: ev.clientY < midY ? r.top : r.bottom,
                    };
                }
            }

            state.dropTarget = best ? { key: best.c.key, position: best.position } : null;

            const line = dropLineRef.current;
            if (!line) return;
            if (best) {
                const r = best.c.el.getBoundingClientRect();
                line.style.display = "block";
                line.style.top = `${best.y - 1}px`;
                line.style.left = `${r.left}px`;
                line.style.width = `${r.width}px`;
            } else {
                line.style.display = "none";
            }
        };

        const onUp = () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";

            const state = dragStateRef.current;
            if (!state) return;

            const line = dropLineRef.current;
            if (line) line.style.display = "none";

            if (!state.isDragging) {
                editor.update(() => {
                    const tn = findTableNode(state.tableEl);
                    if (!tn) {
                        console.warn("[TableSelectHandle] click: no table node");
                        return;
                    }
                    const sel = $createNodeSelection();
                    sel.add(tn.getKey());
                    $setSelection(sel);
                });
            } else if (state.dropTarget) {
                const dropTarget = state.dropTarget;
                editor.update(() => {
                    const tn = findTableNode(state.tableEl);
                    if (!tn) {
                        console.warn("[TableSelectHandle] drag: no table node");
                        return;
                    }
                    const target = $getNodeByKey(dropTarget.key);
                    if (!target || !$isElementNode(target)) {
                        console.warn("[TableSelectHandle] drag: bad target", dropTarget, target);
                        return;
                    }
                    if (dropTarget.position === "before") {
                        target.insertBefore(tn);
                    } else {
                        target.insertAfter(tn);
                    }
                });
            } else {
                console.warn("[TableSelectHandle] drag ended with no drop target");
            }

            dragStateRef.current = null;
        };

        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    };

    const r = hover ? hover.tableEl.getBoundingClientRect() : null;

    return (
        <>
            {r && (
                <button
                    type="button"
                    className="rte-table-handle"
                    style={{
                        position: "fixed",
                        top: r.top - HANDLE_H,
                        left: r.left,
                        width: HANDLE_W,
                        height: HANDLE_H,
                    }}
                    onMouseDown={onMouseDown}
                    onClick={(e) => e.preventDefault()}
                    title="Drag to move, click to select"
                    aria-label="Table handle"
                >
                    <Move size={12} />
                </button>
            )}
            <div
                ref={dropLineRef}
                className="rte-drop-line"
                style={{ display: "none" }}
            />
            {ringBounds && (
                <div
                    className="rte-table-selection-ring"
                    style={{
                        position: "fixed",
                        top: ringBounds.top - 4,
                        left: ringBounds.left - 4,
                        width: ringBounds.width + 8,
                        height: ringBounds.height + 8,
                    }}
                />
            )}
        </>
    );
}
